/**
 * THE single resolver for "line edges": any wall surface that can host edge
 * segments. Those are primary edges (pe), alcove sub-walls (aw), room-corner
 * chamfer diagonals (rc), and alcove-corner chamfer diagonals (ac).
 *
 * Every consumer (hit-handling, drags, panel edits, dimension chips, renderers)
 * resolves a LineEdgeRef through here, so there is exactly one definition of a
 * host line's frame, segment list, and usable span. `lineHosts` lists every
 * host a room has, so there is one definition of those too.
 */

import {
  alcoveCornerLineFrame,
  alcoveWallLineFrame,
  edgeLeg,
  type LineFrame,
  primaryEdgeLineFrame,
  roomCornerLineFrame,
} from './geometry';
import {
  type AlcoveEdgeName,
  CORNER_NAMES,
  type CornerName,
  EDGE_NAMES,
  type EdgeName,
  edgeSegments,
  EMPTY_EDGE,
  type FloorPlanEdgeSegment,
  type FloorPlanRoom,
  type OuterWhich,
  roomCorner,
} from './schemas';

/** Identifies any "line edge" that can hold edge segments. */
export type LineEdgeRef =
  | { k: 'pe'; edge: EdgeName } // primary edge
  | { k: 'aw'; edge: EdgeName; storageIndex: number; wall: AlcoveEdgeName } // alcove sub-wall
  | { k: 'rc'; corner: CornerName } // room-corner chamfer diagonal
  | { k: 'ac'; edge: EdgeName; storageIndex: number; which: OuterWhich }; // alcove-corner chamfer diagonal

/** Structural equality of two line-edge refs (same host line). */
export function sameLineRef(a: LineEdgeRef, b: LineEdgeRef): boolean {
  if (a.k !== b.k) return false;
  switch (a.k) {
    case 'pe':
      return b.k === 'pe' && a.edge === b.edge;
    case 'aw':
      return b.k === 'aw' && a.edge === b.edge && a.storageIndex === b.storageIndex && a.wall === b.wall;
    case 'rc':
      return b.k === 'rc' && a.corner === b.corner;
    case 'ac':
      return b.k === 'ac' && a.edge === b.edge && a.storageIndex === b.storageIndex && a.which === b.which;
  }
}

export interface ResolvedLine {
  ref: LineEdgeRef;
  lf: LineFrame;
  /** The host's segment list, read-only: edit it through the edit catalog. A host with no list yet reads as empty. */
  segments: FloorPlanEdgeSegment[];
  /** Usable span along the line. Primary edges exclude the corner-chamfer legs. */
  minBound: number;
  maxBound: number;
}

/**
 * Resolve a line-edge ref against a shape. Null if the host doesn't exist.
 * Read-only: a host that has no segment list yet (a missing primary edge,
 * alcove sub-wall, or chamfer-diagonal list) resolves to the frozen empty
 * list, and the shape is never written. Edits materialize missing hosts
 * through the edit catalog's structural sharing.
 */
export function resolveLineRef(shape: FloorPlanRoom, ref: LineEdgeRef): ResolvedLine | null {
  let lineFrame: LineFrame | null = null;
  let segments: FloorPlanEdgeSegment[] = EMPTY_EDGE.segments;
  let minBound = 0;
  let maxBound: number | null = null;

  if (ref.k === 'pe') {
    lineFrame = primaryEdgeLineFrame(shape, ref.edge);
    segments = edgeSegments(shape, ref.edge);
    minBound = edgeLeg(shape, ref.edge, true);
    maxBound = lineFrame.length - edgeLeg(shape, ref.edge, false);
  } else if (ref.k === 'aw') {
    const alcove = shape[ref.edge]?.segments[ref.storageIndex];
    if (!alcove) return null;
    lineFrame = alcoveWallLineFrame(shape, ref.edge, ref.storageIndex, ref.wall);
    segments = alcove[ref.wall]?.segments ?? segments;
  } else if (ref.k === 'rc') {
    lineFrame = roomCornerLineFrame(shape, ref.corner);
    segments = roomCorner(shape, ref.corner).segments ?? segments;
  } else {
    const alcove = shape[ref.edge]?.segments[ref.storageIndex];
    if (!alcove) return null;
    lineFrame = alcoveCornerLineFrame(shape, ref.edge, ref.storageIndex, ref.which);
    const chamfer = alcove[ref.which];
    if (!chamfer) return null;
    segments = chamfer.segments ?? segments;
  }
  if (!lineFrame) return null;
  return { ref, lf: lineFrame, segments, minBound, maxBound: maxBound ?? lineFrame.length };
}

/** A host that can hold edge segments, and the segments it holds. */
export interface LineHost {
  ref: LineEdgeRef;
  /** The list the room stores for the host, or the frozen empty list for a host with none yet. */
  segments: FloorPlanEdgeSegment[];
}

/** An alcove's walls and back corners, in boundary-walk order. */
const alcoveHosts = (edge: EdgeName, storageIndex: number, alcove: FloorPlanEdgeSegment): LineHost[] => {
  const wall = (wall: AlcoveEdgeName): LineHost => ({ ref: { k: 'aw', edge, storageIndex, wall }, segments: alcove[wall]?.segments ?? EMPTY_EDGE.segments });
  const corner = (which: OuterWhich): LineHost => ({ ref: { k: 'ac', edge, storageIndex, which }, segments: alcove[which]?.segments ?? EMPTY_EDGE.segments });
  return [wall('leftEdge'), corner('backLeftCorner'), wall('backEdge'), corner('backRightCorner'), wall('rightEdge')];
};

/** A primary edge, followed by its alcoves' walls and back corners. */
const edgeHosts = (room: FloorPlanRoom, edge: EdgeName): LineHost[] => {
  const segments = edgeSegments(room, edge);
  return [
    { ref: { k: 'pe', edge }, segments },
    ...segments.flatMap((segment, storageIndex) => segment.edgeSegmentType === 'alcove' ? alcoveHosts(edge, storageIndex, segment) : []),
  ];
};

/**
 * Every host in the room that can hold edge segments: each primary edge,
 * followed by its alcoves' walls and back corners, then the four room
 * corners. A host need not have a line to hold them on: a square corner has
 * none, and `resolveLineRef` gives null for it.
 */
export const lineHosts = (room: FloorPlanRoom): LineHost[] => [
  ...EDGE_NAMES.flatMap(edge => edgeHosts(room, edge)),
  ...CORNER_NAMES.map((corner): LineHost => ({ ref: { k: 'rc', corner }, segments: roomCorner(room, corner).segments ?? EMPTY_EDGE.segments })),
];

/** The segment list a ref points at (empty array if the host is missing). */
export function lineRefSegments(shape: FloorPlanRoom, ref: LineEdgeRef): FloorPlanEdgeSegment[] {
  return resolveLineRef(shape, ref)?.segments ?? [];
}

/**
 * Bare (segment-free) gaps along a line span. Shared by the add-affordance
 * placement and the clickable-gap hit strips — the single "gaps on a line"
 * routine.
 */
export function lineGaps(
  segments: FloorPlanEdgeSegment[],
  from: number,
  to: number,
  minGap = 0.01,
): { gs: number; ge: number }[] {
  const sorted = [...segments].sort((a, b) => a.position - b.position);
  const gaps: { gs: number; ge: number }[] = [];
  let cursor = from;
  for (const seg of sorted) {
    if (seg.position > cursor + minGap) gaps.push({ gs: cursor, ge: seg.position });
    cursor = Math.max(cursor, seg.position + seg.width);
  }
  if (cursor + minGap < to) gaps.push({ gs: cursor, ge: to });
  return gaps;
}
